import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg, money } from "@/lib/api";
import { YesNo, WinnerLockNote } from "@/components/FancallPanel";

const NEW = "__new__";
const TAGS = { gom: "text-amber-300", fixed: "text-pink-300", soft: "text-sky-300", open: "text-zinc-400" };

const MemberChip = ({ m, on, tag, left, onToggle }) => (
  <button
    type="button"
    data-testid={`claim-member-${m.replace(/\s+/g, "-").toLowerCase()}`}
    aria-pressed={on}
    onClick={onToggle}
    className={`rounded-full border px-3 py-1.5 text-sm transition-colors duration-200 ${on ? "border-pink-500 bg-pink-500/15 text-pink-100" : "border-zinc-700 hover:border-zinc-500"}`}
  >
    {m}
    {tag && <span className={`ml-1 text-[9px] font-bold uppercase ${TAGS[tag]}`}>{tag}</span>}
    <span className="font-mono text-[11px] text-zinc-500 ml-1">{left}</span>
  </button>
);

export default function ClaimDialog({ drop, initial, onClose, onClaimed }) {
  const [joiners, setJoiners] = useState([]);
  const [joinerId, setJoinerId] = useState("");
  const [newName, setNewName] = useState("");
  const isNew = joinerId === NEW;
  const [picked, setPicked] = useState(initial.member ? [initial.member] : []);
  const [ot8, setOt8] = useState(false);
  const [optIn, setOptIn] = useState(null);
  const [busy, setBusy] = useState(false);
  const isFancall = !!drop.has_fancall;
  const isUnit = drop.fill_rule === "unit";
  const fcfs = isUnit || !!drop.limited;
  const noun = isUnit ? "unit" : "member";

  useEffect(() => {
    api.get("/joiners").then((r) => setJoiners(r.data)).catch(() => {});
  }, []);

  const joiner = joiners.find((j) => j.id === joinerId);
  const tagOf = (m) => (!joiner || fcfs ? null : ["gom", "fixed", "soft", "open"].find((t) => joiner[`${t}_claims`]?.includes(m)) || null);
  const prior = drop.fancall_optins?.find((o) => o.joiner_id === joinerId)?.opt_in;
  const lockYes = !!joinerId && drop.fancall_winners?.includes(joinerId);
  const optValue = lockYes ? false : optIn ?? prior ?? null;
  const count = picked.length + (ot8 ? drop.members.length : 0);
  const total = useMemo(() => count * drop.price_per_member, [count, drop.price_per_member]);

  const pickJoiner = (id) => {
    const j = joiners.find((x) => x.id === id);
    setJoinerId(id);
    setOptIn(null);
    setPicked(fcfs ? (initial.member ? [initial.member] : []) : [...new Set([...(initial.member ? [initial.member] : []), ...(j?.fixed_claims || []), ...(j?.soft_claims || [])])]);
  };
  const toggle = (m) => setPicked((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));

  const submit = async () => {
    if (isNew && !newName.trim()) return toast.error("Enter your joiner name");
    if (!joinerId || count === 0) return toast.error(`Choose your name and at least one ${noun}`);
    if (isFancall && optValue === null) return toast.error(isUnit ? "Let us know if you want to opt in for the call" : "Let us know if you want to opt into the fancall");
    setBusy(true);
    let jid = joinerId;
    try {
      if (isNew) {
        const nj = await api.post("/joiners/self", { name: newName.trim() });
        jid = nj.data.id;
        setJoiners((l) => [...l, nj.data]);
        setJoinerId(jid);
        toast.success(`Welcome ${nj.data.name}! You've been added to the joiner list`);
      }
      const members = [...drop.members.filter((m) => picked.includes(m)), ...(ot8 ? ["OT8"] : [])];
      const r = await api.post(`/drops/${drop.id}/claims`, { joiner_id: jid, members, fancall_opt_in: isFancall ? optValue : null });
      const lines = r.data.placed.map((p) => `${p.member === "OT8" && isUnit ? "All 4 units" : p.member} → Set #${p.set_number}${p.status === "pending" ? " (pending)" : p.status === "dropped" ? " (dropped, set didn't fill)" : ""}`).join(", ");
      toast.success(`${r.data.joiner_name}: ${lines}`);
      if (r.data.rejected.length) toast.warning(`No room for: ${r.data.rejected.join(", ")}`);
      onClaimed();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Claim failed"));
      onClaimed();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="claim-dialog" className="bg-zinc-950 border-zinc-800 sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isUnit ? "Claim units" : "Claim members"}</DialogTitle>
          <DialogDescription>{drop.store || drop.title} · {money(drop.price_per_member)} per {noun} · {isUnit ? "first come for everyone; a set goes through once it's 4/4" : drop.limited ? `Limited Drop: first come; a set goes through once it's ${drop.threshold || 8}/8` : "sets fill by claim priority, then claim time"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Your name</Label>
            <Select value={joinerId} onValueChange={pickJoiner}>
              <SelectTrigger data-testid="claim-joiner-select" className="bg-zinc-900"><SelectValue placeholder="Select joiner" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NEW} data-testid="claim-joiner-option-new" className="font-semibold text-pink-300">+ New Joiner</SelectItem>
                {joiners.map((j) => <SelectItem key={j.id} value={j.id} data-testid={`claim-joiner-option-${j.id}`}>{j.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {isNew && (
              <div className="space-y-1">
                <Input data-testid="claim-new-joiner-name" value={newName} maxLength={60} autoFocus placeholder="Enter your joiner name" onChange={(e) => setNewName(e.target.value)} className="bg-zinc-900" />
                <div className="text-xs text-zinc-500">You'll be added to the joiner list, open for all members.</div>
              </div>
            )}
            {joiner && !fcfs && <div className="text-xs text-zinc-500">Fixed and Soft claims are pre-selected; untick any you don't want.</div>}
          </div>
          <div className="space-y-1.5">
            <Label>{isUnit ? "Units" : "Members"}</Label>
            <div className="flex flex-wrap gap-2" data-testid="claim-member-options">
              {drop.members.map((m) => <MemberChip key={m} m={m} on={picked.includes(m)} tag={tagOf(m)} left={drop.member_open[m]} onToggle={() => toggle(m)} />)}
              <button
                type="button"
                data-testid="claim-member-ot8"
                aria-pressed={ot8}
                onClick={() => setOt8((v) => !v)}
                className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors duration-200 ${ot8 ? "border-pink-500 bg-pink-500/15 text-pink-100" : "border-pink-500/40 hover:border-pink-400"}`}
              >
                {isUnit ? "All 4 units" : "OT8"} <span className="font-mono text-[11px] text-zinc-500">{drop.member_open.OT8}</span>
              </button>
            </div>
            <div className="text-xs text-zinc-500">{isUnit ? "Numbers show free slots. All 4 units takes a whole empty set. Every claim is first come, first served." : drop.limited ? "Numbers show free slots. OT8 takes a whole empty set first; every other claim is first come, first served." : "Numbers show free slots. OT8 takes a whole set and goes first; then GoM, Fixed, Soft and Open picks, first come within each."}</div>
          </div>
          {isFancall && (
            <div className="space-y-1.5" data-testid="claim-fancall-question">
              <Label>{isUnit ? "Do you wish to opt in for the call?" : "Do you wish to opt into the fancall?"}</Label>
              {isUnit && <div className="text-xs text-zinc-500">If Yes, each claim is 1 entry for the fancall raffle, up to 4 entries max.</div>}
              {lockYes ? <WinnerLockNote /> : <YesNo value={optValue} onPick={setOptIn} testPrefix="claim-fancall" />}
            </div>
          )}
          <div className="flex justify-between text-sm rounded-xl bg-zinc-900 px-4 py-3">
            <span className="text-zinc-400" data-testid="claim-count">{count} {noun}{count === 1 ? "" : "s"}</span>
            <span className="font-mono font-bold" data-testid="claim-total">{money(total)}</span>
          </div>
          {drop.unfilled_price > drop.price_per_member && (
            <div className="text-xs text-zinc-500 -mt-2" data-testid="claim-unfilled-note">
              Total is for full 8/8 sets. While a set isn't full, each member costs {money(drop.unfilled_price)}.
            </div>
          )}
          <Button data-testid="claim-submit-button" disabled={busy} onClick={submit} className="w-full h-11 rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 hover:opacity-90 text-white font-semibold">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm claim
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
